import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { apiClient } from '@/services/api'
import type { Ticker, PriceData } from '@/types'

export const useMarketStore = defineStore('market', () => {
  // State
  const tickers = ref<Ticker[]>([])
  const prices = ref<Map<string, PriceData>>(new Map())
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const lastUpdate = ref<Date | null>(null)
  const marketStatus = ref({
    isOpen: false,
    nextOpen: null as Date | null,
    nextClose: null as Date | null,
    timezone: 'America/New_York'
  })
  
  // Computed
  const isOpen = computed(() => marketStatus.value.isOpen)
  
  const activeTickers = computed(() => 
    tickers.value.filter(ticker => ticker.is_active)
  )
  
  const tickerCount = computed(() => tickers.value.length)
  
  const priceUpdates = computed(() => {
    const updates: Array<PriceData & { symbol: string }> = []
    prices.value.forEach((price, symbol) => {
      updates.push({ ...price, symbol })
    })
    return updates.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
  })
  
  // Actions
  const fetchTickers = async () => {
    try {
      isLoading.value = true
      error.value = null

      const response = await apiClient.get('/api/v1/tickers')
      // Handle both array and object response formats
      const tickerData = response.data.tickers || response.data
      tickers.value = tickerData.map((ticker: any) => ({
        ...ticker,
        created_at: new Date(ticker.created_at),
        updated_at: ticker.updated_at ? new Date(ticker.updated_at) : null
      }))

      console.log(`Loaded ${tickers.value.length} tickers`)
    } catch (err: any) {
      error.value = err.response?.data?.detail || 'Failed to fetch tickers'
      console.error('Failed to fetch tickers:', err)
    } finally {
      isLoading.value = false
    }
  }
  
  const addTicker = async (symbol: string, name?: string) => {
    try {
      const response = await apiClient.post('/api/v1/tickers', {
        symbol: symbol.toUpperCase(),
        name: name || symbol.toUpperCase(),
        is_active: true
      })
      
      const newTicker = {
        ...response.data,
        created_at: new Date(response.data.created_at),
        updated_at: new Date(response.data.updated_at)
      }
      
      tickers.value.push(newTicker)
      return newTicker
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || 'Failed to add ticker'
      error.value = errorMsg
      throw new Error(errorMsg)
    }
  }
  
  const removeTicker = async (tickerId: number) => {
    try {
      // Capture symbol before removing from list
      const t = tickers.value.find(t => t.id === tickerId)
      await apiClient.delete(`/api/v1/tickers/${tickerId}`)
      tickers.value = tickers.value.filter(ticker => ticker.id !== tickerId)
      if (t) {
        prices.value.delete(t.symbol)
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || 'Failed to remove ticker'
      error.value = errorMsg
      throw new Error(errorMsg)
    }
  }
  
  const toggleTicker = async (tickerId: number, isActive: boolean) => {
    try {
      const response = await apiClient.put(`/api/v1/tickers/${tickerId}`, {
        is_active: isActive
      })

      const updatedTicker = {
        ...response.data,
        created_at: new Date(response.data.created_at),
        updated_at: new Date(response.data.updated_at)
      }

      const index = tickers.value.findIndex(ticker => ticker.id === tickerId)
      if (index !== -1) {
        tickers.value[index] = updatedTicker
      }

      return updatedTicker
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || 'Failed to update ticker'
      error.value = errorMsg
      throw new Error(errorMsg)
    }
  }
  
  const fetchPrices = async (symbols?: string[]) => {
    try {
      const params = symbols ? { symbols: symbols.join(',') } : {}
      const response = await apiClient.get('/api/v1/prices/latest', { params })

      // Handle the actual API response format
      const priceDataArray = Array.isArray(response.data) ? response.data : []

      priceDataArray.forEach((priceData: any) => {
        const price: PriceData = {
          open: parseFloat(priceData.open_price || priceData.current_price || '0'),
          high: parseFloat(priceData.high_price || priceData.current_price || '0'),
          low: parseFloat(priceData.low_price || priceData.current_price || '0'),
          close: parseFloat(priceData.current_price || priceData.close_price || '0'),
          volume: priceData.volume || 0,
          change: parseFloat(priceData.price_change || '0'),
          changePercent: parseFloat(priceData.price_change_percent || '0'),
          timestamp: new Date(priceData.last_updated || priceData.timestamp || new Date())
        }

        prices.value.set(priceData.symbol, price)
      })

      lastUpdate.value = new Date()
      console.log(`Loaded prices for ${priceDataArray.length} tickers`)
    } catch (err: any) {
      error.value = err.response?.data?.detail || 'Failed to fetch prices'
      console.error('Failed to fetch prices:', err)
    }
  }
  
  const updatePrice = (symbol: string, priceData: Partial<PriceData>) => {
    const existing = prices.value.get(symbol)
    if (existing) {
      prices.value.set(symbol, { ...existing, ...priceData })
    } else {
      prices.value.set(symbol, {
        open: 0,
        high: 0,
        low: 0,
        close: 0,
        volume: 0,
        change: 0,
        changePercent: 0,
        timestamp: new Date(),
        ...priceData
      })
    }
    lastUpdate.value = new Date()
  }
  
  const getPrice = (symbol: string): PriceData | null => {
    return prices.value.get(symbol) || null
  }
  
  const fetchMarketStatus = async () => {
    try {
      const response = await apiClient.get('/api/v1/market/status')
      marketStatus.value = {
        isOpen: response.data.is_open || false,
        nextOpen: response.data.next_event === 'open' && response.data.next_event_time
          ? new Date(response.data.next_event_time)
          : null,
        nextClose: response.data.next_event === 'close' && response.data.next_event_time
          ? new Date(response.data.next_event_time)
          : null,
        timezone: response.data.timezone || 'America/New_York'
      }
      console.log('Market status:', marketStatus.value)
    } catch (err: any) {
      console.error('Failed to fetch market status:', err)
      // Default to closed if we can't fetch status
      marketStatus.value.isOpen = false
    }
  }
  
  const fetchHistoricalData = async (symbol: string, days: number = 30) => {
    try {
      const response = await apiClient.get(`/api/v1/prices/history/${symbol}`, {
        params: { days }
      })
      
      return response.data.map((item: any) => ({
        timestamp: new Date(item.timestamp),
        open: parseFloat(item.open_price),
        high: parseFloat(item.high_price),
        low: parseFloat(item.low_price),
        close: parseFloat(item.close_price),
        volume: item.volume,
        change: parseFloat(item.price_change || '0'),
        changePercent: parseFloat(item.price_change_percent || '0')
      }))
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || 'Failed to fetch historical data'
      error.value = errorMsg
      throw new Error(errorMsg)
    }
  }
  
  const clearError = () => {
    error.value = null
  }
  
  const refreshData = async () => {
    await Promise.all([
      fetchTickers(),
      fetchPrices(),
      fetchMarketStatus()
    ])
  }
  
  return {
    // State
    tickers,
    prices,
    isLoading,
    error,
    lastUpdate,
    marketStatus,
    
    // Computed
    isOpen,
    activeTickers,
    tickerCount,
    priceUpdates,
    
    // Actions
    fetchTickers,
    addTicker,
    removeTicker,
    toggleTicker,
    fetchPrices,
    updatePrice,
    getPrice,
    fetchMarketStatus,
    fetchHistoricalData,
    clearError,
    refreshData
  }
})
